import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

export default function AddNewJob() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    company: 'Aurous Consultancy',
    location: '',
    job_type: 'Full-time',
    salary_range: '',
    description: '',
    requirements: '',
  });

  useEffect(() => {
    if (isEditing) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job details');
      navigate('/applied-jobs');
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.location || !formData.description || !formData.requirements) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/jobs/${id}`, formData);
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/jobs`, formData);
        toast.success('Job created successfully');
      }
      navigate('/applied-jobs');
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Remote', 'Internship'];

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="add-job-page">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? 'Edit Job' : 'Add New Job'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEditing ? 'Update job posting details' : 'Create a new job posting'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg gradient-blue">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle>Job Details</CardTitle>
                <CardDescription>Enter the job posting information</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Job Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Senior React Developer"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                data-testid="job-title-input"
              />
            </div>

            {/* Company & Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  placeholder="Company name"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  data-testid="company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  placeholder="e.g., Mumbai, India or Remote"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  data-testid="location-input"
                />
              </div>
            </div>

            {/* Job Type & Salary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job_type">Job Type</Label>
                <Select
                  value={formData.job_type}
                  onValueChange={(value) => handleChange('job_type', value)}
                >
                  <SelectTrigger data-testid="job-type-select">
                    <SelectValue placeholder="Select job type" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary_range">Salary Range</Label>
                <Input
                  id="salary_range"
                  placeholder="e.g., ₹15-25 LPA"
                  value={formData.salary_range}
                  onChange={(e) => handleChange('salary_range', e.target.value)}
                  data-testid="salary-input"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Job Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe the role, responsibilities, and what you're looking for..."
                rows={5}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                data-testid="description-input"
              />
            </div>

            {/* Requirements */}
            <div className="space-y-2">
              <Label htmlFor="requirements">Requirements *</Label>
              <Textarea
                id="requirements"
                placeholder="List the skills, experience, and qualifications required..."
                rows={4}
                value={formData.requirements}
                onChange={(e) => handleChange('requirements', e.target.value)}
                data-testid="requirements-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-btn">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : isEditing ? 'Update Job' : 'Create Job'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
